import React, { useEffect, useState } from "react";
import useTitle from "../hooks/useTitle";
import backend from "../backend";
import EntityLookup from "../components/design-system/EntityLookup";
import IngresoBodega, { MovimientosRecientes } from "../components/IngresoBodega";
import useIsMobile from "../hooks/useIsMobile";

// ─── helpers ──────────────────────────────────────────────────────────────────
const getStockStatus = (stock, min) => {
  if (!min || min === 0) return { color: "#999", label: "sin mín", barColor: "#444" };
  const ratio = stock / min;
  if (ratio <= 0.3) return { color: "#e74c3c", label: "crítico", barColor: "#e74c3c" };
  if (ratio <= 0.6) return { color: "#f39c12", label: "bajo",    barColor: "#f39c12" };
  return                   { color: "#2ecc71", label: "ok",      barColor: "#2ecc71" };
};

// ─── estilos ──────────────────────────────────────────────────────────────────
const S = {
  wrap: {
    display: "block", padding: "18px 22px", background: "#1a1a1a",
    minHeight: "100%", color: "#ddd", fontFamily: "Arial,sans-serif",
    fontSize: 13, textAlign: "left", boxSizing: "border-box", width: "100%",
  },
  card: {
    background: "#242424", border: "1px solid #2e2e2e",
    borderRadius: 8, padding: "14px 16px",
  },
  cardHeader: {
    fontSize: 10, fontWeight: 700, color: "#cbd5e1",
    textTransform: "uppercase", letterSpacing: ".05em",
    marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid #2e2e2e",
    display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center",
  },
  row: {
    display: "flex", flexDirection: "row", alignItems: "center",
    padding: "7px 0", borderBottom: "1px solid #252525", fontSize: 12, gap: 8,
  },
  label: {
    fontSize: 10, fontWeight: 700, color: "#999",
    textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4,
  },
  input: {
    background: "#161616", border: "1px solid #333", borderRadius: 5,
    padding: "7px 10px", fontSize: 12, color: "#ddd",
    fontFamily: "Arial,sans-serif", width: "100%", boxSizing: "border-box",
  },
  sectionTitle: {
    fontSize: 10, fontWeight: 700, color: "#999",
    textTransform: "uppercase", letterSpacing: ".07em", margin: "18px 0 10px",
  },
};

const MATERIAL_TYPES = ["Barra redonda","Barra cuadrada","Barra hexagonal","Plancha","Tubo","Perfil","Otro"];

const emptyMat = () => ({ name:"", materialType:"", dimensions:"", unit:"trozo", stock:"", minStock:"", provider:"" });

// ─── componente principal ─────────────────────────────────────────────────────
const Inventory = () => {
  useTitle("Inventario");
  const isMobile = useIsMobile();

  // producto terminado (piezas con stock)
  const [piezas,      setPiezas]      = useState([]);
  // material bruto
  const [materiales,  setMateriales]  = useState([]);
  const [loading,     setLoading]     = useState(true);

  // forms
  const [showMatForm,   setShowMatForm]   = useState(false);
  const [formMat,       setFormMat]       = useState(emptyMat());
  const [savingMat,     setSavingMat]     = useState(false);
  const [editMatId,     setEditMatId]     = useState(null);
  const [editMatForm,   setEditMatForm]   = useState(emptyMat());
  const [adjustMatId,   setAdjustMatId]   = useState(null);
  const [adjustMatDelta,setAdjustMatDelta]= useState(0);

  // nuevo stock de producto terminado
  const [showStockForm, setShowStockForm] = useState(false);
  const [stockMode,     setStockMode]     = useState("catalog"); // "catalog" | "external"
  const [stockPieceId,  setStockPieceId]  = useState("");
  const [stockQty,      setStockQty]      = useState(1);
  const [stockExtName,  setStockExtName]  = useState("");
  const [stockExtSku,   setStockExtSku]   = useState("");
  const [stockExtPrice, setStockExtPrice] = useState("");
  const [savingStock,   setSavingStock]   = useState(false);

  // ajuste stock pieza
  const [adjustId,     setAdjustId]     = useState(null);
  const [adjustDelta,  setAdjustDelta]  = useState(0);
  // "sumar" (delta, puede ser negativo con el botón −) o "fijar" (dejar el conteo exacto).
  // Existe porque en el teléfono el teclado numérico no tiene signo menos: restar o
  // corregir un conteo era imposible desde el celular (lo encontró Catalina, 31-ago).
  const [adjustMode,   setAdjustMode]   = useState("sumar");
  const [configStockId,setConfigStockId]= useState(null);
  const [detailPiece,  setDetailPiece]  = useState(null);

  // D-Stock-Gestion — revisar alertas y generar PO automáticas
  const [checkingAlerts, setCheckingAlerts] = useState(false);
  const [alertResult,    setAlertResult]    = useState(null);
  const [selectedAlerts, setSelectedAlerts] = useState(() => new Set()); // pieceIds
  const [batchModal,     setBatchModal]     = useState(null); // { rows, provider }

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, mRes] = await Promise.all([
        backend.get("piece"),
        backend.get("rawMaterial"),
      ]);
      setPiezas((pRes.data || []).filter(p => p.status !== "archived" && p.managedStock === true));
      setMateriales(mRes.data || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // filtros de búsqueda en las listas
  const [filtroMat, setFiltroMat] = useState("");
  const [filtroPza, setFiltroPza] = useState("");
  const matchTxt = (txt, f) => (txt || "").toLowerCase().includes(f.toLowerCase());
  // 26-ago (feedback Maxi): las tarjetas KPI filtran las listas al tocarlas
  // (tocar de nuevo limpia). critico/bajo → materiales; con/sin stock → piezas.
  const [kpiFiltro, setKpiFiltro] = useState(null);
  const esCritico = m => m.minStock > 0 && m.stock / m.minStock <= 0.3;
  const esBajo = m => { const r = m.stock / (m.minStock || 1); return m.minStock > 0 && r > 0.3 && r <= 0.6; };
  const pasaKpiMat = m => kpiFiltro === "critico" ? esCritico(m) : kpiFiltro === "bajo" ? esBajo(m) : true;
  const pasaKpiPza = p => kpiFiltro === "con_stock" ? (p.stock || 0) > 0 : kpiFiltro === "sin_stock" ? !(p.stock > 0) : true;

  const materialesVista = (filtroMat.trim()
    ? materiales.filter(m => matchTxt(m.name, filtroMat) || matchTxt(m.materialType, filtroMat) || matchTxt(m.dimensions, filtroMat))
    : materiales).filter(pasaKpiMat);
  const piezasVista = (filtroPza.trim()
    ? piezas.filter(p => matchTxt(p.name, filtroPza) || matchTxt((p.sku || "").replace(/\s/g, ""), filtroPza.replace(/\s/g, "")))
    : piezas).filter(pasaKpiPza);

  // métricas
  const critico   = materiales.filter(m => m.minStock > 0 && m.stock / m.minStock <= 0.3).length;
  const bajo      = materiales.filter(m => { const r = m.stock/(m.minStock||1); return m.minStock > 0 && r > 0.3 && r <= 0.6; }).length;
  const conStock  = piezas.filter(p => (p.stock || 0) > 0).length;
  const sinStock  = piezas.filter(p => !(p.stock > 0)).length;

  // ajustar stock de pieza
  const handleAdjust = async (pieceId) => {
    if (adjustMode === "sumar" && !Number(adjustDelta)) { setAdjustId(null); return; }
    try {
      // vía auditada: queda el movimiento en stockMovements (quién/cuánto/antes/después)
      const res = await backend.post("stock/ingreso", { pieceId, qty: Number(adjustDelta), modo: adjustMode, nota: adjustMode === "fijar" ? "conteo manual /inventory" : "ajuste manual /inventory" });
      setPiezas(prev => prev.map(p => p._id === pieceId ? { ...p, stock: res.data?.stock } : p));
    } catch(e) { console.error(e); alert("Error: " + (e?.response?.data?.error || e.message)); }
    setAdjustId(null); setAdjustDelta(0);
  };

  // guardar nuevo stock de producto terminado
  const handleSaveStock = async () => {
    setSavingStock(true);
    try {
      if (stockMode === "catalog") {
        // sumar stock a pieza existente del catálogo
        if (!stockPieceId) { alert("Selecciona una pieza del catálogo"); setSavingStock(false); return; }
        // vía auditada (stockMovements)
        const res = await backend.post("stock/ingreso", { pieceId: stockPieceId, qty: Number(stockQty), modo: "sumar", nota: "ingreso formulario /inventory" });
        setPiezas(prev => prev.map(p => p._id === stockPieceId ? { ...p, stock: res.data?.stock } : p));
      } else {
        // crear pieza nueva directamente con stock inicial (sin procesos)
        // se marca managedStock=true para que aparezca en /inventario.
        // esImportada=true porque es producto comprado, no fabricado por nosotros.
        if (!stockExtName) { alert("Ingresa el nombre del producto"); setSavingStock(false); return; }
        const res = await backend.post("piece", {
          name:         stockExtName,
          sku:          stockExtSku || `EXT-${Date.now()}`,
          price:        Number(stockExtPrice) || undefined,
          stock:        Number(stockQty),
          status:       "active",
          processes:    [],
          constants:    undefined,
          managedStock: true,
          esImportada:  true,
        });
        setPiezas(prev => [...prev, res.data]);
      }
      // reset form
      setStockPieceId(""); setStockQty(1);
      setStockExtName(""); setStockExtSku(""); setStockExtPrice("");
      setShowStockForm(false);
    } catch(e) {
      console.error(e);
      alert("Error al registrar stock: " + (e?.response?.data || e.message));
    }
    setSavingStock(false);
  };

  // guardar nuevo material
  const handleSaveMat = async () => {
    if (!formMat.name) { alert("Ingresa el nombre del material"); return; }
    setSavingMat(true);
    try {
      const res = await backend.post("rawMaterial", {
        ...formMat,
        stock:    Number(formMat.stock)    || 0,
        minStock: Number(formMat.minStock) || 0,
      });
      setMateriales(prev => [res.data, ...prev]);
      setFormMat(emptyMat()); setShowMatForm(false);
    } catch(e) { console.error(e); alert("Error al guardar"); }
    setSavingMat(false);
  };

  // disparar revisión de alertas → muestra panel con clasificación
  // (compra / fabricación interna / sin material). Usuario decide por fila.
  const handleCheckAlerts = async () => {
    setCheckingAlerts(true);
    setAlertResult(null);
    setSelectedAlerts(new Set());
    try {
      const res = await backend.get("purchaseOrder/check-low-stock");
      setAlertResult(res.data);
    } catch(e) {
      console.error(e);
      setAlertResult({ error: e?.response?.data?.error || e.message });
    }
    setCheckingAlerts(false);
  };

  // Toggle de checkbox en una alerta
  const toggleAlertSelection = (pieceId) => {
    setSelectedAlerts(prev => {
      const next = new Set(prev);
      if (next.has(pieceId)) next.delete(pieceId);
      else next.add(pieceId);
      return next;
    });
  };

  // Abrir modal de cotización agrupada con todas las alertas seleccionadas
  const openBatchModal = () => {
    const rows = (alertResult?.needsPurchase || [])
      .filter(r => selectedAlerts.has(r._id));
    if (rows.length === 0) return;
    setBatchModal({ rows, provider: null, saving: false, err: null });
  };

  // Confirmar y crear PO agrupada
  const submitBatch = async () => {
    if (!batchModal) return;
    setBatchModal(b => ({ ...b, saving: true, err: null }));
    try {
      await backend.post("purchaseOrder/from-piece-alerts", {
        pieceIds: batchModal.rows.map(r => r._id),
        provider: batchModal.provider || undefined,
      });
      setBatchModal(null);
      setSelectedAlerts(new Set());
      handleCheckAlerts();
      load();
    } catch(e) {
      setBatchModal(b => ({ ...b, saving: false, err: e?.response?.data?.error || e.message }));
    }
  };

  // Acción por fila: comprar (genera PO cotizando)
  const handleAlertPurchase = async (pieceId) => {
    try {
      await backend.post("purchaseOrder/from-piece-alerts", { pieceIds: [pieceId] });
      // recargar revisión + lista
      handleCheckAlerts();
      load();
    } catch(e) {
      alert(e?.response?.data?.error || e.message);
    }
  };

  // Acción por fila: fabricar para stock (genera WO interna)
  const handleAlertFabricate = async (pieceId, qty) => {
    try {
      await backend.post("workOrder/from-stock-alert", { pieceId, qty });
      handleCheckAlerts();
      load();
    } catch(e) {
      alert(e?.response?.data?.error || e.message);
    }
  };

  // ajustar stock material bruto (vía auditada: queda en stockMovements)
  const handleAdjustMat = async (matId, delta) => {
    try {
      const res = await backend.patch("rawMaterial/stock", { _id: matId, delta: Number(delta) });
      setMateriales(prev => prev.map(m => m._id === matId ? res.data : m));
    } catch(e) { console.error(e); alert("Error: " + (e?.response?.data?.error || e.message)); }
  };

  const startEditMat = (material) => {
    setEditMatId(material._id);
    setAdjustMatId(null);
    setEditMatForm({
      name: material.name || "", materialType: material.materialType || "",
      dimensions: material.dimensions || "", unit: material.unit || "trozo",
      stock: material.stock ?? 0, minStock: material.minStock ?? 0,
      provider: material.provider || "", grade: material.grade || "",
      lastUnitPrice: material.lastUnitPrice ?? 0, notes: material.notes || "",
    });
  };

  const saveEditMat = async () => {
    try {
      const { data } = await backend.patch("rawMaterial", {
        _id: editMatId, ...editMatForm,
        stock: Number(editMatForm.stock) || 0,
        minStock: Number(editMatForm.minStock) || 0,
        lastUnitPrice: Number(editMatForm.lastUnitPrice) || 0,
      });
      setMateriales(prev => prev.map(m => m._id === editMatId ? data : m));
      setEditMatId(null);
    } catch (e) {
      alert("Error al editar: " + (e?.response?.data?.error || e.message));
    }
  };

  const archiveMat = async (material) => {
    if (!window.confirm(`¿Archivar ${material.name}? Dejará de aparecer en inventario.`)) return;
    try {
      await backend.patch("rawMaterial/archive", { _id: material._id });
      setMateriales(prev => prev.filter(m => m._id !== material._id));
    } catch (e) {
      alert("Error al archivar: " + (e?.response?.data?.error || e.message));
    }
  };

  const setM = k => e => setFormMat(p => ({ ...p, [k]: e.target.value }));

  return (
    <div style={{ ...S.wrap, padding: isMobile ? "12px 10px" : S.wrap.padding }}>

      {/* ── Ingreso rápido de bodega (por código, pensado para celular) ── */}
      <IngresoBodega
        onStockChange={(itemId, nuevoStock, tipo) => {
          if (tipo === "material") {
            setMateriales(prev => prev.map(m => m._id === itemId ? { ...m, stock: nuevoStock } : m));
            return;
          }
          setPiezas(prev => {
            const existe = prev.some(p => p._id === itemId);
            if (existe) return prev.map(p => p._id === itemId ? { ...p, stock: nuevoStock } : p);
            // la pieza no estaba en la lista (recién pasó a managedStock): recargar
            load();
            return prev;
          });
        }}
      />

      {/* ── Bitácora de movimientos (colapsable) ── */}
      <MovimientosRecientes />

      {/* ── Header ── */}
      <div style={{ display:"flex", flexDirection:"row", alignItems:"center", justifyContent:"space-between", marginBottom:18, flexWrap:"wrap", gap:8 }}>
        <div style={{ fontSize:16, fontWeight:700, color:"#fff" }}>Inventario</div>
        <div style={{ display:"flex", flexDirection:"row", gap:8 }}>
          <button
            onClick={handleCheckAlerts}
            disabled={checkingAlerts}
            title="Genera OCs de compra (en cotizando) para piezas con stock bajo y material vinculado"
            style={{
              background:"#2d2000", color:"#f39c12", border:"1px solid #4a3500",
              borderRadius:6, padding:"7px 16px", fontSize:12,
              fontWeight:700, cursor:"pointer", whiteSpace:"nowrap",
              opacity: checkingAlerts ? 0.6 : 1,
            }}
          >{checkingAlerts ? "Revisando…" : "⚠ Revisar alertas"}</button>
          <button
            onClick={() => { setShowStockForm(v => !v); setShowMatForm(false); }}
            style={{
              background:"#0d2a15", color:"#2ecc71", border:"1px solid #0d4020",
              borderRadius:6, padding:"7px 16px", fontSize:12,
              fontWeight:700, cursor:"pointer", whiteSpace:"nowrap",
            }}
          >{showStockForm ? "Cancelar" : "+ Nuevo stock"}</button>
          <button
            onClick={() => { setShowMatForm(v => !v); setShowStockForm(false); }}
            style={{
              background:"#e30613", color:"#fff", border:"none",
              borderRadius:6, padding:"7px 16px", fontSize:12,
              fontWeight:700, cursor:"pointer", whiteSpace:"nowrap",
            }}
          >{showMatForm ? "Cancelar" : "+ Registrar material bruto"}</button>
        </div>
      </div>

      {/* Panel de revisión de alertas con acción por fila */}
      {alertResult && (
        <div style={{
          ...S.card, marginBottom: 14,
          borderColor: alertResult.error ? "#5a1f1f" : "#2d2000",
          background: alertResult.error ? "#1f0a0a" : "#1a1500",
        }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 10 }}>
            <div style={{ fontSize:11, fontWeight:700, color: alertResult.error ? "#e74c3c" : "#f39c12",
              textTransform:"uppercase", letterSpacing:".07em" }}>
              {alertResult.error ? "Error en revisión" : "Alertas de stock bajo"}
            </div>
            <button onClick={() => setAlertResult(null)}
              style={{ background:"transparent", border:"none", color: "#aaa", cursor:"pointer", fontSize:14 }}>×</button>
          </div>
          {alertResult.error ? (
            <div style={{ color:"#e74c3c", fontSize:12 }}>{alertResult.error}</div>
          ) : (
            <>
              {alertResult.needsPurchase?.length > 0 && (
                <AlertSection title="🛒 Comprar a proveedor" color="#3498db"
                  rows={alertResult.needsPurchase}
                  selectable
                  selectedIds={selectedAlerts}
                  onToggle={toggleAlertSelection}
                  batchAction={selectedAlerts.size > 0 ? {
                    label: `Generar cotización (${alertResult.needsPurchase.filter(r => selectedAlerts.has(r._id)).length})`,
                    color: "#3498db",
                    onClick: openBatchModal,
                  } : null}
                  cta={{ label: "Generar PO sola", color: "#3498db", onClick: r => handleAlertPurchase(r._id) }}
                  extra={r => r.materialRef && <span style={{ fontSize:10, color: "#999" }}>· Material: {r.materialRef.name}</span>}
                />
              )}
              {alertResult.needsFabrication?.length > 0 && (
                <AlertSection title="🔧 Fabricar para stock" color="#a78bfa"
                  rows={alertResult.needsFabrication}
                  cta={{ label: "Crear OT interna", color: "#a78bfa", onClick: r => handleAlertFabricate(r._id, r.reorderQty) }}
                />
              )}
              {alertResult.missingMaterial?.length > 0 && (
                <AlertSection title="⚠ Sin material vinculado (importadas)" color="#e74c3c"
                  rows={alertResult.missingMaterial}
                  hint="Definí material en /catálogo o desmarcá 'Importada'."
                />
              )}
              {alertResult.alreadyHandled?.length > 0 && (
                <AlertSection title="✓ Ya en proceso" color="#2ecc71"
                  rows={alertResult.alreadyHandled}
                  extra={r => (
                    <span style={{ fontSize:10, color: "#999" }}>
                      {r.po && `· PO #${r.po.number}`}
                      {r.wo && `· OT #${r.wo.number}`}
                    </span>
                  )}
                />
              )}
              {!alertResult.needsPurchase?.length && !alertResult.needsFabrication?.length
                && !alertResult.missingMaterial?.length && !alertResult.alreadyHandled?.length && (
                <div style={{ color: "#aaa", fontStyle:"italic", fontSize:12 }}>
                  Nada que reponer — todo el stock managed está sobre el mínimo.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Métricas ── */}
      <div style={{ display:"flex", flexDirection:"row", gap:8, marginBottom:18 }}>
        {[
          { key:"critico",   label:"Mat. crítico",  value:critico,  sub:"bajo mínimo",       color:"#e74c3c" },
          { key:"bajo",      label:"Mat. bajo",     value:bajo,     sub:"cerca del mínimo",  color:"#f39c12" },
          { key:"con_stock", label:"Piezas en stock",value:conStock, sub:"listas para despacho",color:"#2ecc71" },
          { key:"sin_stock", label:"Piezas sin stock",value:sinStock,sub:"requieren fabricación",color: "#999"  },
        ].map(m => (
          <div key={m.label}
            onClick={() => setKpiFiltro(kpiFiltro === m.key ? null : m.key)}
            className="t-02 tap-card"
            style={{
              flex:"1 1 0", background: kpiFiltro === m.key ? "#2a2a2a" : "#242424",
              border:`1px solid ${kpiFiltro === m.key ? m.color : "#2e2e2e"}`,
              borderRadius:8, padding:"12px 14px", cursor:"pointer",
            }}>
            <div style={{ fontSize:10, color: "#aaa", marginBottom:4, textTransform:"uppercase", letterSpacing:".05em" }}>{m.label}</div>
            <div style={{ fontSize:24, fontWeight:700, color:m.color }}>{m.value}</div>
            <div style={{ fontSize:10, color: kpiFiltro === m.key ? m.color : "#999", marginTop:2 }}>
              {kpiFiltro === m.key ? "✕ quitar filtro" : m.sub}
            </div>
          </div>
        ))}
      </div>

      {/* ── Formulario nuevo stock de producto terminado ── */}
      {showStockForm && (
        <div style={{ ...S.card, marginBottom:16 }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#2ecc71", textTransform:"uppercase", letterSpacing:".07em", marginBottom:12 }}>Registrar stock de producto terminado</div>

          {/* tabs modo */}
          <div style={{ display:"flex", flexDirection:"row", borderBottom:"1px solid #2e2e2e", marginBottom:14 }}>
            {[["catalog","Del catálogo"],["external","Producto externo / importado"]].map(([key,label]) => (
              <button key={key} onClick={() => setStockMode(key)} style={{
                padding:"6px 16px", background:"transparent", border:"none",
                borderBottom: stockMode===key ? "2px solid #2ecc71" : "2px solid transparent",
                color: stockMode===key ? "#2ecc71" : "#555",
                fontSize:12, fontWeight: stockMode===key ? 700 : 400, cursor:"pointer",
              }}>{label}</button>
            ))}
          </div>

          {stockMode === "catalog" ? (
            /* pieza existente */
            <div style={{ display:"flex", flexDirection:"row", gap:10, marginBottom:12 }}>
              <div style={{ flex:2 }}>
                <div style={S.label}>Pieza del catálogo</div>
                <select value={stockPieceId} onChange={e => setStockPieceId(e.target.value)} style={S.input}>
                  <option value="">Seleccionar pieza…</option>
                  {piezas.map(p => (
                    <option key={p._id} value={p._id}>
                      {p.sku ? `${p.sku} — ` : ""}{p.name} (stock actual: {p.stock || 0})
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex:1 }}>
                <div style={S.label}>Cantidad a ingresar</div>
                <input
                  type="number" min={1} value={stockQty}
                  onChange={e => setStockQty(e.target.value)}
                  style={S.input}
                />
              </div>
            </div>
          ) : (
            /* producto externo */
            <div>
              <div style={{ display:"flex", flexDirection:"row", gap:10, marginBottom:10 }}>
                <div style={{ flex:2 }}>
                  <div style={S.label}>Nombre del producto</div>
                  <input value={stockExtName} onChange={e => setStockExtName(e.target.value)} placeholder="Ej: Válvula importada ACME 3/4" style={S.input} />
                </div>
                <div style={{ flex:1 }}>
                  <div style={S.label}>SKU / código</div>
                  <input value={stockExtSku} onChange={e => setStockExtSku(e.target.value)} placeholder="Ej: IMP-0042" style={S.input} />
                </div>
              </div>
              <div style={{ display:"flex", flexDirection:"row", gap:10, marginBottom:10 }}>
                <div style={{ flex:1 }}>
                  <div style={S.label}>Cantidad</div>
                  <input type="number" min={1} value={stockQty} onChange={e => setStockQty(e.target.value)} style={S.input} />
                </div>
                <div style={{ flex:1 }}>
                  <div style={S.label}>Precio unitario ref.</div>
                  <input type="number" value={stockExtPrice} onChange={e => setStockExtPrice(e.target.value)} placeholder="0" style={S.input} />
                </div>
                <div style={{ flex:2 }}>
                  <div style={S.label}>Nota</div>
                  <div style={{ fontSize:11, color: "#999", paddingTop:9 }}>
                    Se creará como pieza en el catálogo con stock inicial. Podrás agregarle procesos y precio después.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div style={{ display:"flex", flexDirection:"row", gap:8, justifyContent:"flex-end" }}>
            <button onClick={() => setShowStockForm(false)} style={{ background:"transparent", color: "#cbd5e1", border:"1px solid #444", borderRadius:6, padding:"7px 14px", fontSize:12, cursor:"pointer" }}>Cancelar</button>
            <button onClick={handleSaveStock} disabled={savingStock} style={{ background:"#0d2a15", color:"#2ecc71", border:"1px solid #0d4020", borderRadius:6, padding:"7px 18px", fontSize:12, fontWeight:700, cursor:"pointer", opacity:savingStock?0.7:1 }}>
              {savingStock ? "Guardando…" : "Registrar stock"}
            </button>
          </div>
        </div>
      )}

      {/* ── Formulario nuevo material bruto ── */}
      {showMatForm && (
        <div style={{ ...S.card, marginBottom:16 }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#e30613", textTransform:"uppercase", letterSpacing:".07em", marginBottom:12 }}>Nuevo material bruto</div>

          {/* fila 1: tipo + descripción/especificación */}
          <div style={{ display:"flex", flexDirection:"row", gap:10, marginBottom:10 }}>
            <div style={{ flex:1 }}>
              <div style={S.label}>Tipo de material</div>
              <select value={formMat.materialType} onChange={setM("materialType")} style={S.input}>
                <option value="">Seleccionar</option>
                {MATERIAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ flex:3 }}>
              <div style={S.label}>Especificación</div>
              <input
                value={formMat.name}
                onChange={setM("name")}
                placeholder="Ej: SAE 4140 diámetro 2″ largo 150mm"
                style={S.input}
              />
            </div>
          </div>

          {/* fila 2: cantidad + unidad + stock mínimo + proveedor */}
          <div style={{ display:"flex", flexDirection:"row", gap:10, marginBottom:12 }}>
            <div style={{ flex:1 }}>
              <div style={S.label}>Cantidad</div>
              <input value={formMat.stock} onChange={setM("stock")} type="number" placeholder="0" style={S.input} />
            </div>
            <div style={{ flex:1 }}>
              <div style={S.label}>Unidad</div>
              <input value={formMat.unit} onChange={setM("unit")} placeholder="trozo, kg, metro…" style={S.input} />
            </div>
            <div style={{ flex:1 }}>
              <div style={S.label}>Stock mínimo</div>
              <input value={formMat.minStock} onChange={setM("minStock")} type="number" placeholder="0" style={S.input} />
            </div>
            <div style={{ flex:2 }}>
              <div style={S.label}>Proveedor habitual</div>
              <input value={formMat.provider} onChange={setM("provider")} placeholder="Ej: Aceros Bravo" style={S.input} />
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"row", gap:8, justifyContent:"flex-end" }}>
            <button onClick={() => setShowMatForm(false)} style={{ background:"transparent", color: "#cbd5e1", border:"1px solid #444", borderRadius:6, padding:"7px 14px", fontSize:12, cursor:"pointer" }}>Cancelar</button>
            <button onClick={handleSaveMat} disabled={savingMat} style={{ background:"#e30613", color:"#fff", border:"none", borderRadius:6, padding:"7px 18px", fontSize:12, fontWeight:700, cursor:"pointer", opacity:savingMat?0.7:1 }}>
              {savingMat ? "Guardando…" : "Registrar material"}
            </button>
          </div>
        </div>
      )}

      {batchModal && (
        <BatchCotizacionModal
          batch={batchModal}
          onProviderChange={p => setBatchModal(b => ({ ...b, provider: p }))}
          onCancel={() => setBatchModal(null)}
          onConfirm={submitBatch}
        />
      )}

      {loading ? (
        <div style={{ textAlign:"center", padding:"40px 0", color: "#999" }}>Cargando inventario…</div>
      ) : (
        <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", gap:12, alignItems: isMobile ? "stretch" : "flex-start" }}>

          {/* ── Material bruto ── */}
          <div style={{ ...S.card, flex:1 }}>
            <div style={S.cardHeader}>
              <span>Material bruto</span>
              <span style={{ fontSize:11, color: "#999" }}>{materialesVista.length} de {materiales.length}</span>
            </div>
            <input
              style={{ ...S.input, marginBottom:8 }}
              placeholder="🔍 Filtrar material…"
              value={filtroMat}
              onChange={e => setFiltroMat(e.target.value)}
            />
            {materialesVista.length === 0 ? (
              <div style={{ textAlign:"center", padding:"20px 0", color: "#999", fontSize:12 }}>{materiales.length ? "Sin resultados para el filtro" : "Sin materiales registrados"}</div>
            ) : (
              materialesVista.map((m, i) => {
                const st = getStockStatus(m.stock, m.minStock);
                const pct = m.minStock > 0 ? Math.min(100, Math.round((m.stock / m.minStock) * 100)) : 50;
                return (
                  <div key={m._id} style={{ ...S.row, borderBottom: i === materialesVista.length-1 ? "none" : "1px solid #252525", flexWrap:"wrap" }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ color:"#fff", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.name}</div>
                      {/* 30-sep-2026 (Maxi): el trozo se identifica de un vistazo con el SKU de la pieza
                          para la que se compra (va en `dimensions`, que también entra en el buscador). */}
                      {(m.materialType || m.dimensions) && (
                        <div style={{ fontSize:11, color: "#999", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {[m.materialType, m.dimensions].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                    {/* barra */}
                    <div style={{ width:50, height:4, background:"#2a2a2a", borderRadius:2, overflow:"hidden", flexShrink:0 }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:st.barColor, borderRadius:2 }} />
                    </div>
                    <span style={{ fontFamily:"monospace", fontSize:11, color:"#bbb", flexShrink:0 }}>
                      {m.stock}/{m.minStock} {m.unit}
                    </span>
                    <span style={{ fontSize:10, fontWeight:700, color:st.color, minWidth:40, textAlign:"right", flexShrink:0 }}>{st.label}</span>
                    {/* botones +/- */}
                    <div style={{ display:"flex", flexDirection:"row", gap:4, flexShrink:0 }}>
                      <button onClick={() => handleAdjustMat(m._id, -1)} className="no-touch-min" style={{ width:30, height:30, border:"none", background:"#2d1515", color:"#e74c3c", borderRadius:4, cursor:"pointer", fontSize:13, fontWeight:700 }}>−</button>
                      <button onClick={() => handleAdjustMat(m._id, +1)} className="no-touch-min" style={{ width:30, height:30, border:"none", background:"#0d2a15", color:"#2ecc71", borderRadius:4, cursor:"pointer", fontSize:13, fontWeight:700 }}>+</button>
                      <button onClick={() => { setAdjustMatId(adjustMatId === m._id ? null : m._id); setAdjustMatDelta(0); setEditMatId(null); }}
                        style={{ width:30, height:30, border:"none", background:"#2a2a2a", color:"#ddd", borderRadius:4, cursor:"pointer" }} title="Ajustar cantidad">±</button>
                      <button onClick={() => startEditMat(m)}
                        style={{ width:30, height:30, border:"none", background:"#0d1a2d", color:"#3498db", borderRadius:4, cursor:"pointer" }} title="Editar material">✎</button>
                      <button onClick={() => archiveMat(m)}
                        style={{ width:30, height:30, border:"none", background:"#2d1515", color:"#e74c3c", borderRadius:4, cursor:"pointer" }} title="Archivar material">⌫</button>
                    </div>
                    {adjustMatId === m._id && (
                      <div style={{ width:"100%", display:"flex", justifyContent:"flex-end", gap:5 }}>
                        <input type="number" value={adjustMatDelta} onChange={e => setAdjustMatDelta(e.target.value)} autoFocus
                          placeholder="Cantidad ±" style={{ ...S.input, width:110, padding:"4px 7px" }} />
                        <button className="bini-btn bini-btn-save" onClick={async () => {
                          if (Number(adjustMatDelta)) await handleAdjustMat(m._id, adjustMatDelta);
                          setAdjustMatId(null); setAdjustMatDelta(0);
                        }}>Aplicar</button>
                        <button className="bini-btn bini-btn-cancel" onClick={() => setAdjustMatId(null)}>Cancelar</button>
                      </div>
                    )}
                    {editMatId === m._id && (
                      <div style={{ width:"100%", padding:"10px", background:"#1d1d1d", border:"1px solid #333", borderRadius:6 }}>
                        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 1fr", gap:8 }}>
                          <MatInput label="Nombre" value={editMatForm.name} onChange={v => setEditMatForm(f => ({ ...f, name:v }))} />
                          <div><div style={S.label}>Tipo</div><select style={S.input} value={editMatForm.materialType} onChange={e => setEditMatForm(f => ({ ...f, materialType:e.target.value }))}><option value="">Seleccionar</option>{MATERIAL_TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
                          <MatInput label="Grado" value={editMatForm.grade} onChange={v => setEditMatForm(f => ({ ...f, grade:v }))} />
                          <MatInput label="Dimensiones" value={editMatForm.dimensions} onChange={v => setEditMatForm(f => ({ ...f, dimensions:v }))} />
                          <MatInput label="Unidad" value={editMatForm.unit} onChange={v => setEditMatForm(f => ({ ...f, unit:v }))} />
                          <MatInput label="Stock mínimo" type="number" value={editMatForm.minStock} onChange={v => setEditMatForm(f => ({ ...f, minStock:v }))} />
                          <MatInput label="Proveedor" value={editMatForm.provider} onChange={v => setEditMatForm(f => ({ ...f, provider:v }))} />
                          <MatInput label="Último precio unitario" type="number" value={editMatForm.lastUnitPrice} onChange={v => setEditMatForm(f => ({ ...f, lastUnitPrice:v }))} />
                          <MatInput label="Notas" value={editMatForm.notes} onChange={v => setEditMatForm(f => ({ ...f, notes:v }))} />
                        </div>
                        <div style={{ display:"flex", justifyContent:"flex-end", gap:6, marginTop:8 }}>
                          <button className="bini-btn bini-btn-cancel" onClick={() => setEditMatId(null)}>Cancelar</button>
                          <button className="bini-btn bini-btn-save" onClick={saveEditMat}>Guardar cambios</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* ── Producto terminado (piezas) ── */}
          <div style={{ ...S.card, flex:1 }}>
            <div style={S.cardHeader}>
              <span>Producto terminado en stock</span>
              <span style={{ fontSize:11, color: "#999" }}>{piezasVista.length} de {piezas.length}</span>
            </div>
            <input
              style={{ ...S.input, marginBottom:8 }}
              placeholder="🔍 Filtrar por SKU o nombre…"
              value={filtroPza}
              onChange={e => setFiltroPza(e.target.value)}
            />
            {piezas.length === 0 ? (
              <div style={{ textAlign:"center", padding:"28px 16px", color: "#999", fontSize:12 }}>
                <div style={{ fontSize:22, marginBottom:8 }}>📦</div>
                <div style={{ color: "#999", marginBottom:6 }}>No hay piezas con stock gestionado</div>
                <div style={{ color:"#3a3a3a", fontSize:11 }}>
                  Ve al <strong style={{ color:"#3498db" }}>Catálogo</strong>, edita una pieza<br/>
                  y activa <strong style={{ color:"#2ecc71" }}>"Gestionar stock"</strong> para verla aquí.
                </div>
              </div>
            ) : (
              piezasVista.map((p, i) => {
                const stock = p.stock || 0;
                const reorderPoint = p.reorderPoint || 0;
                const needsReorder = p.minStock > 0 && stock <= reorderPoint;
                const barPct = p.minStock > 0 ? Math.min(100, Math.round((stock/p.minStock)*100)) : Math.min(100, stock*10);
                const barColor = needsReorder ? "#e74c3c" : stock > 5 ? "#2ecc71" : stock > 0 ? "#f39c12" : "#555";
                return (
                  <div key={p._id} style={{ borderBottom: i === piezasVista.length-1 ? "none" : "1px solid #252525" }}>
                    {/* fila principal */}
                    <div style={{ display:"flex", flexDirection:"row", alignItems:"center", padding:"8px 0", gap:8, flexWrap:"wrap" }}>
                      {/* SKU + nombre — clickeable */}
                      <div
                        style={{ flex:1, minWidth:0, cursor:"pointer" }}
                        onClick={() => setDetailPiece(detailPiece?._id === p._id ? null : p)}
                      >
                        <div style={{ display:"flex", flexDirection:"row", alignItems:"center", gap:6, marginBottom:2 }}>
                          {p.sku ? (
                            <span style={{ fontSize:10, fontFamily:"monospace", fontWeight:700, padding:"1px 6px", borderRadius:4, background:"#0d1a2d", color:"#3498db", border:"1px solid #0d2a4a", flexShrink:0 }}>{p.sku}</span>
                          ) : (
                            <span style={{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:4, background:"#222", color: "#999", border:"1px solid #333", flexShrink:0 }}>Sin SKU</span>
                          )}
                          <span style={{ fontSize:12, fontWeight:700, color: needsReorder ? "#e74c3c" : "#fff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                            {needsReorder ? "⚠ " : ""}{p.name}
                          </span>
                        </div>
                        {(p.material || p.geometry) && (
                          <div style={{ fontSize:11, color: "#999" }}>{[p.material, p.geometry].filter(Boolean).join(" · ")}</div>
                        )}
                      </div>

                      {/* barra */}
                      <div style={{ width:50, height:4, background:"#2a2a2a", borderRadius:2, overflow:"hidden", flexShrink:0 }}>
                        <div style={{ height:"100%", width:`${barPct}%`, background:barColor, borderRadius:2 }} />
                      </div>

                      {/* controles de stock */}
                      {adjustId === p._id ? (
                        <div style={{ display:"flex", flexDirection:"row", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                          <div style={{ display:"flex", borderRadius:5, overflow:"hidden", border:"1px solid #333" }}>
                            <button onClick={() => setAdjustMode("sumar")} style={{ height:30, padding:"0 8px", border:"none", cursor:"pointer", fontSize:11, fontWeight:700, background: adjustMode==="sumar" ? "#0d2a15" : "#222", color: adjustMode==="sumar" ? "#2ecc71" : "#888" }}>+/−</button>
                            <button onClick={() => { setAdjustMode("fijar"); setAdjustDelta(p.stock || 0); }} style={{ height:30, padding:"0 8px", border:"none", cursor:"pointer", fontSize:11, fontWeight:700, background: adjustMode==="fijar" ? "#0d1a2d" : "#222", color: adjustMode==="fijar" ? "#3498db" : "#888" }}>Dejar en</button>
                          </div>
                          <button onClick={() => setAdjustDelta(d => Number(d||0) - 1)} style={{ width:34, height:34, border:"none", background:"#3a1d1d", color:"#e74c3c", borderRadius:5, cursor:"pointer", fontSize:16, fontWeight:700 }}>−</button>
                          <input type="text" inputMode="numeric" pattern="-?[0-9]*" value={adjustDelta} onChange={e => setAdjustDelta(e.target.value.replace(/[^0-9-]/g, ""))}
                            autoFocus style={{ ...S.input, width:55, textAlign:"center", padding:"6px" }} />
                          <button onClick={() => setAdjustDelta(d => Number(d||0) + 1)} style={{ width:34, height:34, border:"none", background:"#0d2a15", color:"#2ecc71", borderRadius:5, cursor:"pointer", fontSize:16, fontWeight:700 }}>+</button>
                          <button onClick={() => handleAdjust(p._id)} style={{ height:34, padding:"0 12px", border:"none", background:"#0d2a15", color:"#2ecc71", borderRadius:5, cursor:"pointer", fontSize:12, fontWeight:700 }}>OK</button>
                          <button onClick={() => { setAdjustId(null); setAdjustDelta(0); setAdjustMode("sumar"); }} style={{ height:34, width:34, border:"none", background:"#333", color: "#cbd5e1", borderRadius:5, cursor:"pointer", fontSize:13 }}>×</button>
                          {adjustMode==="fijar" && <div style={{ fontSize:10, color:"#3498db", width:"100%" }}>El stock quedará exactamente en ese número (hoy: {p.stock || 0}).</div>}
                        </div>
                      ) : configStockId === p._id ? (
                        <div style={{ display:"flex", flexDirection:"row", gap:6, alignItems:"center", flexWrap:"wrap", width:"100%", paddingTop:6 }}>
                          <div style={{ fontSize:10, color:"#3498db" }}>Mantener:</div>
                          <input type="number" placeholder="cantidad" defaultValue={p.minStock||""} id={`min-${p._id}`}
                            style={{ ...S.input, width:70, textAlign:"center", padding:"3px 6px" }} />
                          <div style={{ fontSize:10, color:"#f39c12" }}>Reponer cuando queden:</div>
                          <input type="number" placeholder="cantidad" defaultValue={p.reorderPoint||""} id={`reorder-${p._id}`}
                            style={{ ...S.input, width:70, textAlign:"center", padding:"3px 6px" }} />
                          <button onClick={async () => {
                            const minStock = Number(document.getElementById(`min-${p._id}`)?.value)||0;
                            const reorderPoint = Number(document.getElementById(`reorder-${p._id}`)?.value)||0;
                            try {
                              await backend.patch("piece", { _id:p._id, minStock, reorderPoint });
                              setPiezas(prev => prev.map(pp => pp._id===p._id ? {...pp, minStock, reorderPoint} : pp));
                            } catch(e) { console.error(e); }
                            setConfigStockId(null);
                          }} style={{ height:26, padding:"0 10px", border:"none", background:"#0d1a2d", color:"#3498db", borderRadius:4, cursor:"pointer", fontSize:11, fontWeight:700 }}>Guardar</button>
                          <button onClick={() => setConfigStockId(null)} style={{ height:26, width:26, border:"none", background:"#333", color: "#cbd5e1", borderRadius:4, cursor:"pointer", fontSize:12 }}>×</button>
                        </div>
                      ) : (
                        <>
                          <span style={{ fontSize:12, fontWeight:700, color:needsReorder?"#e74c3c":stock>0?"#2ecc71":"#555", flexShrink:0 }}>{stock} un.</span>
                          {p.minStock > 0 && <span style={{ fontSize:10, color: "#999", flexShrink:0 }}>/ {p.minStock} · rep:{reorderPoint}</span>}
                          <div style={{ display:"flex", flexDirection:"row", gap:3 }}>
                            <button onClick={() => { setAdjustId(p._id); setAdjustDelta(0); setConfigStockId(null); }}
                              style={{ width:26, height:26, border:"none", background:"#2a2a2a", color: "#cbd5e1", borderRadius:5, cursor:"pointer", fontSize:11 }} title="Ajustar stock">±</button>
                            <button onClick={() => { setConfigStockId(p._id); setAdjustId(null); setDetailPiece(null); }}
                              style={{ width:26, height:26, border:"none", background:"#0d1a2d", color:"#3498db", borderRadius:5, cursor:"pointer", fontSize:13 }} title="Configurar stock">⚙</button>
                          </div>
                        </>
                      )}
                    </div>

                    {/* panel de detalle expandible */}
                    {detailPiece?._id === p._id && (
                      <div style={{ background:"#1e1e1e", borderRadius:6, padding:"10px 12px", marginBottom:8, border:"1px solid #2e2e2e" }}>
                        <div style={{ display:"flex", flexDirection:"row", gap:24, flexWrap:"wrap", fontSize:12 }}>
                          {p.price && <div><span style={{ color: "#999" }}>Precio: </span><span style={{ color:"#2ecc71", fontWeight:700 }}>${p.price.toLocaleString("es-CL")}</span></div>}
                          {p.material && <div><span style={{ color: "#999" }}>Material: </span><span style={{ color:"#bbb" }}>{p.material}</span></div>}
                          {p.geometry && <div><span style={{ color: "#999" }}>Geometría: </span><span style={{ color:"#bbb" }}>{p.geometry}</span></div>}
                          {p.details && <div><span style={{ color: "#999" }}>Descripción: </span><span style={{ color:"#bbb" }}>{p.details}</span></div>}
                        </div>
                        {p.processes?.length > 0 && (
                          <div style={{ marginTop:8 }}>
                            <div style={{ fontSize:10, color: "#999", textTransform:"uppercase", letterSpacing:".05em", marginBottom:6 }}>Procesos</div>
                            <div style={{ display:"flex", flexDirection:"row", flexWrap:"wrap", gap:6 }}>
                              {p.processes.map((pr, pi) => (
                                <div key={pi} style={{ background:"#242424", border:"1px solid #2e2e2e", borderRadius:5, padding:"4px 10px", fontSize:11 }}>
                                  <span style={{ color:"#fff", fontWeight:600 }}>{pr.name || `Proceso ${pi+1}`}</span>
                                  {pr.duration && <span style={{ color: "#999", marginLeft:6 }}>{pr.duration} min</span>}
                                  {pr.external && <span style={{ color:"#a78bfa", marginLeft:6, fontSize:10 }}>externo</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

        </div>
      )}
    </div>
  );
};

const MatInput = ({ label, value, onChange, type = "text" }) => (
  <div>
    <div style={S.label}>{label}</div>
    <input style={S.input} type={type} value={value} onChange={e => onChange(e.target.value)} />
  </div>
);

// ─── Modal: generar cotización agrupada con N alertas + 1 proveedor ─────────
// El proveedor es opcional (puede asignarse después en /compras). El PDF al
// proveedor solo expone material/qty/unidad, sin SKU de pieza.
const BatchCotizacionModal = ({ batch, onProviderChange, onCancel, onConfirm }) => (
  <div style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050,
  }}>
    <div style={{
      background: "#1a1a1a", color: "#ddd", borderRadius: 8,
      padding: "20px 24px", width: "min(680px, 92vw)",
      maxHeight: "90vh", overflow: "auto",
      border: "1px solid #2e2e2e", fontFamily: "Arial, sans-serif", fontSize: 13,
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 6 }}>
        Generar cotización al proveedor
      </div>
      <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 14 }}>
        Se creará UNA OC de compra (status: cotizando) con {batch.rows.length} ítem{batch.rows.length>1?"s":""}.
        El PDF al proveedor solo muestra material y cantidades — sin SKUs internos.
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, color: "#cbd5e1", marginBottom: 4, textTransform: "uppercase", letterSpacing: ".05em" }}>
          Proveedor (opcional)
        </div>
        <EntityLookup
          type="provider"
          value={batch.provider}
          onChange={onProviderChange}
          placeholder="Buscar proveedor por nombre o RUT…"
        />
        <div style={{ fontSize: 10, color: "#aaa", marginTop: 4, fontStyle: "italic" }}>
          Si lo dejás vacío, podrás asignarlo después en /compras antes de enviar.
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, color: "#cbd5e1", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>
          Ítems a cotizar
        </div>
        <div style={{
          background: "#0d0d0d", border: "1px solid #2e2e2e",
          borderRadius: 6, padding: "8px 12px",
        }}>
          {batch.rows.map((r, i) => (
            <div key={r._id} style={{
              fontSize: 12, padding: "5px 0",
              borderBottom: i < batch.rows.length - 1 ? "1px solid #1f1f1f" : "none",
            }}>
              <div style={{ color: "#fff" }}>
                {r.materialRef?.name || <em style={{ color:"#e74c3c" }}>(sin material)</em>}
                {r.materialRef?.materialType && (
                  <span style={{ color: "#cbd5e1", marginLeft: 4 }}>· {r.materialRef.materialType}</span>
                )}
              </div>
              <div style={{ fontSize: 10, color: "#aaa" }}>
                Cantidad: {r.reorderQty} {r.materialRef?.unit || ""}
              </div>
            </div>
          ))}
        </div>
      </div>

      {batch.err && (
        <div style={{ color: "#e74c3c", fontSize: 11, marginBottom: 10 }}>{batch.err}</div>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} disabled={batch.saving} style={{
          background: "transparent", color: "#cbd5e1",
          border: "1px solid #444", borderRadius: 6,
          padding: "7px 14px", fontSize: 12, cursor: "pointer",
        }}>Cancelar</button>
        <button onClick={onConfirm} disabled={batch.saving} style={{
          background: "#3498db", color: "#fff",
          border: "none", borderRadius: 6,
          padding: "7px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer",
          opacity: batch.saving ? 0.6 : 1,
        }}>{batch.saving ? "Creando…" : "Crear cotización"}</button>
      </div>
    </div>
  </div>
);

// ─── Sub-componente: sección del panel de alertas ───────────────────────────
const AlertSection = ({ title, color, rows, cta, hint, extra,
  selectable, selectedIds, onToggle, batchAction }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
      <div style={{ fontSize:11, fontWeight:700, color }}>
        {title} <span style={{ color: "#999", fontWeight:400 }}>({rows.length})</span>
      </div>
      {batchAction && (
        <button onClick={batchAction.onClick} style={{
          background: batchAction.color, color: "#fff",
          border: "none", borderRadius: 4,
          padding: "5px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer",
        }}>{batchAction.label}</button>
      )}
    </div>
    {hint && <div style={{ fontSize:10, color: "#aaa", marginBottom:6, fontStyle:"italic" }}>{hint}</div>}
    {rows.map(r => {
      const checked = selectable && selectedIds?.has(r._id);
      return (
        <div key={r._id} style={{
          display:"flex", alignItems:"center", gap:10,
          padding:"6px 10px",
          background: checked ? "#0d2535" : "#0d0d0d",
          borderRadius:5,
          border:`1px solid ${checked ? "#3498db" : "#1f1f1f"}`,
          marginBottom:4,
        }}>
          {selectable && (
            <input type="checkbox"
              checked={!!checked}
              onChange={() => onToggle(r._id)}
              style={{ cursor:"pointer", width:14, height:14 }} />
          )}
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12 }}>
              {r.sku && (
                <span style={{ fontFamily:"monospace", fontSize:10, padding:"1px 6px",
                  background:"#0d1a2d", color:"#3498db", borderRadius:3 }}>{r.sku}</span>
              )}
              <span style={{ color:"#fff", fontWeight:600 }}>{r.name}</span>
              {extra && extra(r)}
            </div>
            <div style={{ fontSize:10, color: "#aaa", marginTop:2 }}>
              Stock: <span style={{ color:"#e74c3c" }}>{r.stock}</span> / mín {r.reorderPoint} · pedir {r.reorderQty}
            </div>
          </div>
          {cta && !checked && (
            <button onClick={() => cta.onClick(r)} style={{
              background: `${cta.color}22`, color: cta.color,
              border:`1px solid ${cta.color}55`, borderRadius:4,
              padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer",
            }}>{cta.label}</button>
          )}
        </div>
      );
    })}
  </div>
);

export default Inventory;
